import {CommonModule} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Component, inject, OnInit, signal} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {TranslateModule} from '@ngx-translate/core';
import {SharedFilterModule} from '@progress/kendo-angular-grid';
import {FilterOperator} from '@progress/kendo-data-query';
import {
  composeRequestParams,
  ConfigService,
  handleError,
  Q9TrimValueDirective,
  SessionService,
  SnackBarService,
  User
} from '@q9elements/ui-kit/common';
import {
  BaseGridFilterComponent,
  FilterDescriptorWithMetadata,
  GridFilterOperatorSelectComponent,
  pickFilterOperators,
  UserInfoComponent
} from '@q9elements/ui-kit/components';
import {find} from 'lodash';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  switchMap,
  take,
  tap
} from 'rxjs';

@Component({
  standalone: true,
  selector: 'q9ma-user-filter',
  imports: [
    CommonModule,
    MatInputModule,
    MatAutocompleteModule,
    FormsModule,
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    Q9TrimValueDirective,
    UserInfoComponent,
    SharedFilterModule,
    MatSelectModule,
    TranslateModule,
    GridFilterOperatorSelectComponent
  ],
  templateUrl: './user-filter.component.html'
})
export class UserFilterComponent extends BaseGridFilterComponent implements OnInit {
  filterForm = new FormGroup({
    operator: new FormControl(FilterOperator.EqualTo),
    value: new FormControl<User | string>('')
  });

  public readonly operators = pickFilterOperators([
    FilterOperator.EqualTo,
    FilterOperator.NotEqualTo
  ]);

  public readonly isLoading = signal(false);
  public readonly options = signal<User[]>([]);
  private sessionService = inject(SessionService);

  constructor(
    private http: HttpClient,
    private snackBarService: SnackBarService
  ) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initSearchControlObserver();
  }

  public formatAssignee = (value: User | string | null): string => {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      const user = this.options().find(user => user.id === value);

      return user ? this.getDisplayName(user) : value;
    }

    return this.getDisplayName(value);
  };

  private getDisplayName(user: User): string {
    return user.firstName || user.lastName
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
      : user.email;
  }

  protected override initFilter(): void {
    const currentFilter = find(this.filter.filters, {
      field: this.field
    }) as FilterDescriptorWithMetadata;

    if (!currentFilter) {
      return void 0;
    }

    const {value, metadata = {}} = currentFilter;

    let {operator} = currentFilter;

    if (!operator) {
      operator = FilterOperator.EqualTo;
    }

    this.filterForm.patchValue({operator: <FilterOperator>operator});

    let selectedUser: User | string = metadata[this.field] || value;

    if (typeof selectedUser === 'object') {
      return this.filterForm.patchValue({value: selectedUser});
    }

    this.fetchUsers()
      .pipe(take(1))
      .subscribe(users => {
        this.options.set(users);

        selectedUser = (find(users, {id: selectedUser}) as User) || '';

        if (selectedUser) {
          this.filterForm.patchValue({value: selectedUser});
        }
      });
  }

  protected override initFilterChangesObserver() {
    this.filterForm.valueChanges
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(({operator, value}) => {
        const user = value as User;

        this.applyFilter(user.id, <FilterOperator>operator, {
          [this.field]: user
        });
      });
  }

  private initSearchControlObserver() {
    this.filterForm
      .get('value')!
      .valueChanges.pipe(
        this.takeUntilComponentDestroy(),
        filter(search => typeof search === 'string' && search!.length > 1),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(search => this.fetchUsers(<string>search!))
      )
      .subscribe(users => this.options.set(users));
  }

  private fetchUsers(search?: string) {
    this.isLoading.set(true);
    const params = search ? composeRequestParams({search}) : {};

    return this.http
      .get<User[]>(
        `${ConfigService.environment.API_ENDPOINT}/organizations/${this.sessionService.teamId()}/users`,
        {
          params
        }
      )
      .pipe(
        handleError(),
        tap(() => this.isLoading.set(false)),
        catchError(error => {
          this.snackBarService.open(error);
          this.isLoading.set(false);

          return EMPTY;
        })
      );
  }
}
